import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { BrowserRouter } from 'react-router';

import { useGetEnv } from '@/api/util/useGetEnv.util.ts';
import { VersionChecker } from '@/components/versionChecker/VersionChecker.comp.tsx';
import { QueryClientWrapper } from '@/config/QueryClientWrapper.comp.tsx';
import { knowMeTheme } from '@/config/theme/theme.ts';
import DictionariesContextProvider from '@/context/dictionariesContext/DictionariesContext.provider.tsx';
import PageContextProvider from '@/context/pageContext/PageContext.provider.tsx';
import { UserContextProvider } from '@/context/userContext/UserContextProvider.util.tsx';
import { ErrorBoundary } from '@/errorHandling/ErrorBoundary.comp.tsx';
import Layout from '@/layouts/private/Layout.tsx';
import { AppRoutes } from '@/routes/AppRoutes.comp';
import { MaintenanceView } from '@/views/maintenance/Maintenance.view.tsx';

dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  weekStart: 0,
});
dayjs.extend(utc);

const nonce = document.querySelector("meta[property='csp-nonce']")?.getAttribute('content') ?? '';

const cache = createCache({
  key: 'css',
  nonce,
  prepend: true,
});

const AppContent = () => {
  const { env, isLoading } = useGetEnv();

  if (isLoading) {
    return <CssBaseline />;
  }

  if (env?.maintenanceMode) {
    return (
      <>
        <CssBaseline />
        <MaintenanceView />
      </>
    );
  }

  return (
    <>
      <VersionChecker />
      <BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
        <PageContextProvider>
          <UserContextProvider>
            <DictionariesContextProvider>
              <Layout>
                <AppRoutes />
              </Layout>
            </DictionariesContextProvider>
          </UserContextProvider>
        </PageContextProvider>
      </BrowserRouter>
    </>
  );
};

function App() {
  return (
    <CacheProvider value={cache}>
      <LocalizationProvider adapterLocale="en" dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={knowMeTheme}>
          <ErrorBoundary>
            <QueryClientWrapper>
              <AppContent />
            </QueryClientWrapper>
          </ErrorBoundary>
        </ThemeProvider>
      </LocalizationProvider>
    </CacheProvider>
  );
}

export default App;
